import React, { useState, useEffect, useMemo } from 'react';
import {
  LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer,
  CartesianGrid, ComposedChart, Bar, ReferenceLine, Legend
} from 'recharts';
import { Flame, Trophy, Target, TrendingUp, TrendingDown } from 'lucide-react';
import { tradingPlanApi } from '../../services/api';
import useVisiblePolling from '../../hooks/useVisiblePolling';

const RANGE_OPTIONS = [
  { label: '30D', days: 30 },
  { label: '90D', days: 90 },
  { label: '1Y',  days: 365 },
];

const fmtDate = (iso) => {
  if (!iso) return '';
  const [, m, d] = iso.split('-');
  return `${m}/${d}`;
};

const fmtMoney = (n) => {
  if (n === null || n === undefined) return '--';
  const sign = n < 0 ? '-' : '';
  return `${sign}$${Math.abs(n).toFixed(2)}`;
};

const StatCard = ({ icon: Icon, label, value, sub, color = '#ef4444' }) => (
  <div style={{
    background: 'rgba(255,255,255,0.04)',
    border: '1px solid rgba(255,255,255,0.08)',
    borderRadius: 12,
    padding: '16px 18px',
    display: 'flex',
    alignItems: 'center',
    gap: 14,
    flex: '1 1 0',
    minWidth: 180,
  }}>
    <div style={{
      width: 40, height: 40, borderRadius: 10,
      background: `${color}22`, color,
      display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
    }}>
      <Icon size={20} />
    </div>
    <div style={{ minWidth: 0 }}>
      <div style={{ color: '#9ca3af', fontSize: 12, textTransform: 'uppercase', letterSpacing: '0.04em' }}>{label}</div>
      <div style={{ color: '#fff', fontSize: 22, fontWeight: 700, lineHeight: 1.2 }}>{value}</div>
      {sub && <div style={{ color: '#6b7280', fontSize: 12, marginTop: 2 }}>{sub}</div>}
    </div>
  </div>
);

const HabitTrends = () => {
  const [days, setDays] = useState(90);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchTrends = async () => {
    try {
      const trends = await tradingPlanApi.getHabitTrends(days);
      setData(trends);
    } catch (err) {
      console.error('Failed to load habit trends:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setLoading(true);
    fetchTrends();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [days]);

  // Auto-refresh while visible — share-the-pattern
  useVisiblePolling(fetchTrends, { intervalMs: 60000, deps: [days] });

  // Build timeline with 7-day moving average for the line chart
  const chartData = useMemo(() => {
    if (!data?.timeline) return [];
    const window = 7;
    const scores = data.timeline.map(t => t.score);
    return data.timeline.map((t, i) => {
      const slice = scores.slice(Math.max(0, i - window + 1), i + 1).filter(s => s !== null);
      const avg = slice.length ? slice.reduce((a, b) => a + b, 0) / slice.length : null;
      return {
        date: t.date,
        label: fmtDate(t.date),
        score: t.score,
        ma: avg !== null ? Math.round(avg * 10) / 10 : null,
        pnl: t.pnl,
      };
    });
  }, [data]);

  const sortedRules = useMemo(() => {
    if (!data?.per_rule) return [];
    return [...data.per_rule].sort((a, b) => b.percent - a.percent);
  }, [data]);

  if (loading && !data) {
    return (
      <div style={{ padding: 40, textAlign: 'center', color: '#9ca3af' }}>
        Loading trends...
      </div>
    );
  }

  if (!data || data.rules_count === 0) {
    return (
      <div style={{ padding: 40, textAlign: 'center', color: '#9ca3af' }}>
        Add some trading plan rules and start logging habits to unlock your trend charts.
      </div>
    );
  }

  if (data.logged_days === 0) {
    return (
      <div style={{ padding: 40, textAlign: 'center', color: '#9ca3af' }}>
        No habit logs yet in the last {data.days} days. Log a few days and your trends will populate here.
      </div>
    );
  }

  const { streaks = {}, correlation = {} } = data;
  const corrDelta = (correlation.high_discipline_avg_pnl ?? null) !== null && (correlation.low_discipline_avg_pnl ?? null) !== null
    ? correlation.high_discipline_avg_pnl - correlation.low_discipline_avg_pnl
    : null;

  return (
    <div style={{ padding: '8px 0', color: '#e5e7eb' }} data-testid="habit-trends">
      {/* Range selector */}
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 6, marginBottom: 16 }}>
        {RANGE_OPTIONS.map(opt => (
          <button
            key={opt.days}
            onClick={() => setDays(opt.days)}
            data-testid={`trends-range-${opt.label}`}
            style={{
              padding: '6px 14px',
              borderRadius: 8,
              border: '1px solid rgba(255,255,255,0.1)',
              background: days === opt.days ? 'linear-gradient(135deg, #ef4444, #dc2626)' : 'rgba(255,255,255,0.04)',
              color: '#fff',
              fontSize: 13,
              fontWeight: 600,
              cursor: 'pointer',
              transition: 'all 0.15s',
            }}
          >{opt.label}</button>
        ))}
      </div>

      {/* Streak / summary row */}
      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 24 }}>
        <StatCard
          icon={Flame}
          label="Current Streak"
          value={`${streaks.current_streak || 0} days`}
          sub={`Days at ≥${streaks.good_threshold || 80}% discipline`}
          color="#f97316"
        />
        <StatCard
          icon={Trophy}
          label="Best Streak"
          value={`${streaks.best_streak || 0} days`}
          sub={`In the last ${data.days} days`}
          color="#fbbf24"
        />
        <StatCard
          icon={Target}
          label="Days Logged"
          value={`${data.logged_days} / ${data.days}`}
          sub={`${Math.round((data.logged_days / data.days) * 100)}% consistency`}
          color="#10b981"
        />
      </div>

      {/* Discipline-over-time line chart */}
      <div style={{
        background: 'rgba(255,255,255,0.03)',
        border: '1px solid rgba(255,255,255,0.08)',
        borderRadius: 12,
        padding: 20,
        marginBottom: 20,
      }}>
        <h3 style={{ margin: 0, fontSize: 16, fontWeight: 600, color: '#fff' }}>Discipline Score Over Time</h3>
        <p style={{ margin: '4px 0 16px', fontSize: 13, color: '#9ca3af' }}>
          Daily score (faded) with 7-day moving average (bold).
        </p>
        <div style={{ height: 280 }}>
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={chartData} margin={{ top: 8, right: 12, left: 0, bottom: 0 }}>
              <CartesianGrid stroke="rgba(255,255,255,0.06)" vertical={false} />
              <XAxis dataKey="label" stroke="#6b7280" tick={{ fontSize: 11 }} interval="preserveStartEnd" />
              <YAxis stroke="#6b7280" domain={[0, 100]} tick={{ fontSize: 11 }} unit="%" />
              <Tooltip
                contentStyle={{ background: '#0f172a', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 8, fontSize: 13 }}
                labelStyle={{ color: '#9ca3af' }}
                formatter={(val, name) => {
                  if (val === null || val === undefined) return ['—', name];
                  return [`${val}%`, name === 'score' ? 'Daily score' : '7-day avg'];
                }}
              />
              <ReferenceLine y={streaks.good_threshold || 80} stroke="#10b981" strokeDasharray="4 4" />
              <Line type="monotone" dataKey="score" stroke="#6b7280" strokeWidth={1.5} dot={false} connectNulls={false} />
              <Line type="monotone" dataKey="ma" stroke="#ef4444" strokeWidth={2.5} dot={false} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Discipline vs P&L correlation */}
      <div style={{
        background: 'rgba(255,255,255,0.03)',
        border: '1px solid rgba(255,255,255,0.08)',
        borderRadius: 12,
        padding: 20,
        marginBottom: 20,
      }}>
        <h3 style={{ margin: 0, fontSize: 16, fontWeight: 600, color: '#fff' }}>Discipline ↔ P&amp;L Correlation</h3>
        <p style={{ margin: '4px 0 16px', fontSize: 13, color: '#9ca3af' }}>
          Daily discipline score (line) overlaid with daily P&amp;L (bars).
        </p>

        {(correlation.high_discipline_avg_pnl !== null || correlation.low_discipline_avg_pnl !== null) && (
          <div style={{
            display: 'flex', gap: 16, flexWrap: 'wrap',
            background: 'rgba(16, 185, 129, 0.06)',
            border: '1px solid rgba(16, 185, 129, 0.2)',
            borderRadius: 8,
            padding: '12px 16px',
            marginBottom: 16,
            fontSize: 14,
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <TrendingUp size={16} style={{ color: '#10b981' }} />
              <span style={{ color: '#9ca3af' }}>Days ≥80% discipline:</span>
              <strong style={{ color: '#10b981' }}>
                {fmtMoney(correlation.high_discipline_avg_pnl)} avg
              </strong>
              <span style={{ color: '#6b7280', fontSize: 12 }}>({correlation.high_days} days)</span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <TrendingDown size={16} style={{ color: '#ef4444' }} />
              <span style={{ color: '#9ca3af' }}>Days &lt;50% discipline:</span>
              <strong style={{ color: '#ef4444' }}>
                {fmtMoney(correlation.low_discipline_avg_pnl)} avg
              </strong>
              <span style={{ color: '#6b7280', fontSize: 12 }}>({correlation.low_days} days)</span>
            </div>
            {corrDelta !== null && (
              <div style={{ marginLeft: 'auto', color: corrDelta >= 0 ? '#10b981' : '#ef4444', fontWeight: 700 }}>
                {corrDelta >= 0 ? '+' : ''}{fmtMoney(corrDelta)} edge from discipline
              </div>
            )}
          </div>
        )}

        <div style={{ height: 280 }}>
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart data={chartData} margin={{ top: 8, right: 12, left: 0, bottom: 0 }}>
              <CartesianGrid stroke="rgba(255,255,255,0.06)" vertical={false} />
              <XAxis dataKey="label" stroke="#6b7280" tick={{ fontSize: 11 }} interval="preserveStartEnd" />
              <YAxis yAxisId="left" stroke="#6b7280" domain={[0, 100]} tick={{ fontSize: 11 }} unit="%" />
              <YAxis yAxisId="right" orientation="right" stroke="#6b7280" tick={{ fontSize: 11 }} />
              <Tooltip
                contentStyle={{ background: '#0f172a', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 8, fontSize: 13 }}
                labelStyle={{ color: '#9ca3af' }}
                formatter={(val, name) => {
                  if (name === 'pnl') return [fmtMoney(val), 'P&L'];
                  if (name === 'ma') return val !== null ? [`${val}%`, '7-day discipline'] : ['—', '7-day discipline'];
                  return [val, name];
                }}
              />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              <Bar yAxisId="right" dataKey="pnl" name="Daily P&L"
                   fill="#3b82f6"
                   shape={(props) => {
                     const { x, y, width, height, payload } = props;
                     const v = payload.pnl || 0;
                     const fill = v >= 0 ? '#10b981' : '#ef4444';
                     return <rect x={x} y={v >= 0 ? y : y + height} width={width} height={Math.abs(height)} fill={fill} opacity={0.7} />;
                   }} />
              <Line yAxisId="left" type="monotone" dataKey="ma" name="7-day discipline" stroke="#ef4444" strokeWidth={2} dot={false} />
            </ComposedChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Per-rule heatmap + table */}
      <div style={{
        background: 'rgba(255,255,255,0.03)',
        border: '1px solid rgba(255,255,255,0.08)',
        borderRadius: 12,
        padding: 20,
      }}>
        <h3 style={{ margin: 0, fontSize: 16, fontWeight: 600, color: '#fff' }}>Per-Rule Adherence</h3>
        <p style={{ margin: '4px 0 16px', fontSize: 13, color: '#9ca3af' }}>
          Best to worst over the last {data.days} days. Hover the squares to see specific dates.
        </p>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {sortedRules.map(rule => {
            const grid = data.rule_grid.find(g => g.rule_id === rule.rule_id);
            return (
              <div key={rule.rule_id} style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <div style={{ flex: '0 0 220px', minWidth: 0 }}>
                  <div style={{
                    color: '#fff', fontSize: 14, fontWeight: 600,
                    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                  }} title={rule.rule_text}>
                    {rule.rule_text || '(unnamed rule)'}
                  </div>
                  <div style={{ fontSize: 12, color: rule.percent >= 80 ? '#10b981' : rule.percent >= 50 ? '#fbbf24' : '#ef4444', fontWeight: 700 }}>
                    {rule.percent}% · {rule.followed}/{rule.total} days
                  </div>
                </div>
                <div style={{
                  flex: 1, display: 'grid',
                  gridTemplateColumns: `repeat(${grid?.days?.length || 0}, minmax(4px, 1fr))`,
                  gap: 2,
                }}>
                  {(grid?.days || []).map((d, i) => {
                    const bg = d.status === 'followed' ? '#10b981'
                             : d.status === 'broken'   ? '#ef4444'
                             : 'rgba(255,255,255,0.06)';
                    const opacity = d.status === 'none' ? 1 : 0.85;
                    return (
                      <div
                        key={i}
                        title={`${d.date}: ${d.status === 'none' ? 'no log' : d.status}`}
                        style={{ height: 14, background: bg, opacity, borderRadius: 2 }}
                      />
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>

        <div style={{ display: 'flex', gap: 16, marginTop: 16, fontSize: 12, color: '#9ca3af' }}>
          <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <span style={{ width: 12, height: 12, background: '#10b981', borderRadius: 2 }} /> Followed
          </span>
          <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <span style={{ width: 12, height: 12, background: '#ef4444', borderRadius: 2 }} /> Broke
          </span>
          <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <span style={{ width: 12, height: 12, background: 'rgba(255,255,255,0.06)', borderRadius: 2 }} /> No log
          </span>
        </div>
      </div>
    </div>
  );
};

export default HabitTrends;
